import classNames from 'classnames'
import { useId, useMemo, useRef } from 'react'
import { ErrorCode, useDropzone } from 'react-dropzone'
import { Trans, useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'

import { Text } from '../Text'
import { Icon } from '../Icon'
import { Button } from '../Button'

import { FileUploadProps, Rejection } from './types'
import { formatBytes } from './formatBytes'
import { Rejections } from './Rejections'

export const FileUpload = ({
  color = 'primary',
  maxFiles = 1,
  label,
  ...props
}: FileUploadProps) => {
  const id = useId()

  const { t } = useTranslation()

  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    fileRejections,
    isDragActive,
    isDragReject,
  } = useDropzone({
    multiple: false,
    maxFiles,
    ...props,
  })

  const rootProps = getRootProps()

  const acceptedFileTypes = useMemo(
    () =>
      props.accept
        ? Object.values(props.accept)
            .map((x) => x.join(', '))
            .join(', ')
        : undefined,
    [props.accept],
  )

  const acceptedMaxFileSize = useMemo(
    () => (props.maxSize ? formatBytes(props.maxSize, 0) : undefined),
    [props.maxSize],
  )

  const acceptedMinFileSize = useMemo(
    () => (props.minSize ? formatBytes(props.minSize, 0) : undefined),
    [props.minSize],
  )

  const rejections: Rejection[] = useMemo(() => {
    const errorMap: { [key in ErrorCode]: string } = {
      'file-too-large': t('file_upload.error.file_too_big', {
        size: acceptedMaxFileSize,
      }),
      'file-too-small': t('file_upload.error.file_too_small', {
        size: acceptedMinFileSize,
      }),
      'file-invalid-type': t('file_upload.error.file_invalid_type', {
        types: acceptedFileTypes,
      }),
      'too-many-files': t('file_upload.error.too_many_files', {
        count: maxFiles,
      }),
    }

    return fileRejections.map((rejection) => ({
      filename: rejection.file.name,
      errors: rejection.errors.map((x) => errorMap[x.code as ErrorCode]),
    }))
  }, [fileRejections])

  const handleRemoveFile = (file: File) => {
    acceptedFiles.length = 0
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        data-testid="dropzone"
        className={classNames(
          'file-upload',
          color === 'secondary' && 'file-upload--color-secondary',
          isDragReject && 'file-upload--invalid',
          isDragActive && 'file-upload--drag-active',
        )}
        {...rootProps}
      >
        <motion.div
          animate={{
            scale: isDragActive ? 1.02 : 1,
          }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="w-full h-full"
        >
          <input {...getInputProps()} autoComplete="off" id={id} />
          <div className="flex flex-col gap-4 items-center justify-center min-h-[162px] md:min-h-[126px]">
            <AnimatePresence mode="wait">
              {acceptedFiles.length && !rejections.length ? (
                <motion.div
                  key="success-state"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="flex flex-col gap-1 items-center w-full max-w-[268px]"
                >
                  <motion.div
                    initial={{ y: 0, opacity: 0 }}
                    animate={{ y: -16, opacity: 1 }}
                    exit={{ y: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="absolute"
                  >
                    <Text variant="body-sm">{t('file_upload.success')}</Text>
                  </motion.div>

                  <motion.div
                    initial={{ y: 0, opacity: 0 }}
                    animate={{ y: 8, opacity: 1 }}
                    exit={{ y: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut', delay: 0.05 }}
                    className="flex items-center justify-between p-3 bg-surface-tertiary-default w-full"
                  >
                    <div className="flex items-center gap-3">
                      <Icon.File />
                      <Text variant="body-sm" className="text-gray-900">
                        {acceptedFiles[0].name}
                      </Text>
                    </div>
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        handleRemoveFile(acceptedFiles[0])
                      }}
                      className="w-5 h-5 p-0 text-black bg-transparent hover:bg-transparent"
                    >
                      <Icon.Trash />
                    </Button>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="upload-state"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="flex flex-col items-center"
                >
                  <Icon.Upload width={16} height={16} />
                  <div className="text-center">
                    <Text as="label" variant="body-md" htmlFor={id}>
                      <Trans components={{ bold: <b className="underline" /> }}>
                        {label ?? t('file_upload.drag_and_drop_upload')}
                      </Trans>
                    </Text>
                    {acceptedFileTypes && (
                      <Text variant="body-sm">
                        {t('file_upload.accepted_files', {
                          files: acceptedFileTypes,
                        })}
                      </Text>
                    )}
                    {acceptedMaxFileSize && (
                      <Text variant="body-sm">
                        {t('file_upload.max_size', {
                          size: acceptedMaxFileSize,
                        })}
                      </Text>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
      {rejections.length > 0 && <Rejections rejections={rejections} />}
    </div>
  )
}
